/**
 * Mappers for reconciling UI types with Database types
 *
 * These functions transform between:
 * - DbLead      (database schema - snake_case)
 * - Opportunity (UI schema - camelCase, enriched pipeline fields)
 */

import type { DbLead, DbLeadCreate, DbLeadUpdate } from "@/types/database";
import { toUuidOrNull } from "@/lib/uuid";
import {
  formatDisplayReference,
  formatLeadReference,
  type Opportunity,
  type OpportunityActivity,
  type OpportunityStage,
  type OwnershipChange,
  type StageChange,
} from "@/data/opportunityTypes";
import type { ProjectType } from "@/data/types";

// ── Stage mapping ─────────────────────────────────────────────────────────

export const LEAD_STAGE_FROM_DB: Record<string, OpportunityStage> = {
  "Lead":                  "Lead",
  "New Lead":              "Lead",
  "New":                   "Lead",
  "Contacted":             "Lead",
  "Appointment Scheduled": "Appointment Scheduled",
  "Appointment Completed": "Appointment Completed",
  "In Consultation":       "Appointment Completed",
  "Follow-Up Needed":      "Follow-Up Needed",
  "Quote Created":         "Follow-Up Needed",
  "Closed Won":            "Closed Won",
  "Sold (Won)":            "Closed Won",
  "Closed Lost":           "Closed Lost",
  "Lost":                  "Closed Lost",
};

export const LEAD_STAGE_TO_DB: Record<OpportunityStage, string> = {
  "Lead":                  "Lead",
  "Quote Created":         "Quote Created",
  "Appointment Scheduled": "Appointment Scheduled",
  "Appointment Completed": "Appointment Completed",
  "Follow-Up Needed":      "Follow-Up Needed",
  "Closed Won":            "Closed Won",
  "Closed Lost":           "Closed Lost",
};

function mapDbStageToUi(stage: string): OpportunityStage {
  return LEAD_STAGE_FROM_DB[stage] ?? "Lead";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function toStringOrFallback(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}

function toStageChange(value: unknown): StageChange | null {
  if (!isRecord(value)) return null;

  const toStage = toStringOrFallback(value.toStage);
  if (!toStage) return null;

  return {
    id: toStringOrFallback(value.id),
    fromStage: value.fromStage == null ? null : mapDbStageToUi(toStringOrFallback(value.fromStage)),
    toStage: mapDbStageToUi(toStage),
    changedAt: toStringOrFallback(value.changedAt),
    changedBy: toStringOrFallback(value.changedBy),
  };
}

function toOwnershipChange(value: unknown): OwnershipChange | null {
  if (!isRecord(value)) return null;

  const toRep = toStringOrFallback(value.toRep);
  if (!toRep) return null;

  return {
    id: toStringOrFallback(value.id),
    fromRep: value.fromRep == null ? null : (toStringOrFallback(value.fromRep) || null),
    toRep,
    changedAt: toStringOrFallback(value.changedAt),
    changedBy: toStringOrFallback(value.changedBy),
  };
}

function toOpportunityActivity(value: unknown): OpportunityActivity | null {
  if (!isRecord(value)) return null;

  const type = toStringOrFallback(value.type);
  const description = toStringOrFallback(value.description);
  if (!type || !description) return null;

  return {
    id: toStringOrFallback(value.id),
    type: type as OpportunityActivity["type"],
    description,
    createdAt: toStringOrFallback(value.createdAt),
    createdBy: toStringOrFallback(value.createdBy),
  };
}

function normalizeStageHistory(history: DbLead["stage_history"]): StageChange[] {
  return (history ?? []).map(toStageChange).filter((item): item is StageChange => item !== null);
}

function normalizeOwnershipHistory(history: DbLead["ownership_history"]): OwnershipChange[] {
  return (history ?? []).map(toOwnershipChange).filter((item): item is OwnershipChange => item !== null);
}

function normalizeActivities(activities: DbLead["activities"]): OpportunityActivity[] {
  return (activities ?? []).map(toOpportunityActivity).filter((item): item is OpportunityActivity => item !== null);
}

export function serializeStageHistory(history: Opportunity["stageHistory"] | undefined): DbLead["stage_history"] {
  return (history ?? []).map((item) => ({
    id: item.id,
    fromStage: item.fromStage,
    toStage: item.toStage,
    changedAt: item.changedAt,
    changedBy: item.changedBy,
  }));
}

function serializeOwnershipHistory(history: Opportunity["ownershipHistory"] | undefined): DbLead["ownership_history"] {
  return (history ?? []).map((item) => ({
    id: item.id,
    fromRep: item.fromRep,
    toRep: item.toRep,
    changedAt: item.changedAt,
    changedBy: item.changedBy,
  }));
}

export function serializeActivities(activities: Opportunity["activities"] | undefined): DbLead["activities"] {
  return (activities ?? []).map((item) => ({
    id: item.id,
    type: item.type,
    description: item.description,
    createdAt: item.createdAt,
    createdBy: item.createdBy,
  }));
}

// ── DB → UI ───────────────────────────────────────────────────────────────

export function toUiOpportunity(db: DbLead): Opportunity {
  const stage: OpportunityStage = LEAD_STAGE_FROM_DB[db.stage] ?? "Lead";
  const fullAddress = [db.address, db.city, `${db.state} ${db.zip}`]
    .filter(Boolean)
    .join(", ");

  return {
    quoteId: db.linked_quote_id ?? "",
    leadId: db.id,
    id: db.id,
    // linked_quote_id is a UUID, not an OPP-XXX string — don't pass it to
    // formatDisplayReference. The merge in useLeads overrides this with the
    // quote's real quote_number (OPP-XXXX) when a quote is found.
    displayReference: formatLeadReference(db.id),
    // Customer
    primaryName:     db.primary_name,
    secondaryName:   db.secondary_name ?? "",
    primaryPhone:    db.primary_phone,
    secondaryPhone:  db.secondary_phone ?? "",
    primaryEmail:    db.primary_email ?? "",
    secondaryEmail:  db.secondary_email ?? "",
    // Address
    address: db.address,
    city:    db.city,
    state:   db.state,
    zip:     db.zip,
    // Project
    projectType: (db.project_type as ProjectType) ?? "Other",
    leadSource:  db.lead_source ?? "",
    // Lifecycle
    stage,
    stageHistory: normalizeStageHistory(db.stage_history),
    ownershipHistory: normalizeOwnershipHistory(db.ownership_history),
    // Ownership & activity
    assignedRep: db.assigned_rep ?? "",
    activities: normalizeActivities(db.activities),
    // Financial
    totalEstimate: 0,
    // Metadata
    notes:     db.notes ?? "",
    createdAt: db.created_at,
    updatedAt: db.updated_at,
    // Links
    linkedJobId:    db.linked_job_id ?? undefined,
    convertedJobId: db.linked_job_id ?? undefined,
    // Appointment
    appointmentTime: db.appointment_time ?? undefined,
    // Backward-compat aliases
    customerName:    db.primary_name,
    customerPhone:   db.primary_phone,
    customerEmail:   db.primary_email ?? "",
    customerAddress: fullAddress,
    customerId:      db.customer_id ?? undefined,
    organizationId:  db.organization_id ?? undefined,
    status:          stage,
  };
}

// ── UI → DB (create) ──────────────────────────────────────────────────────

export function toDbLeadCreate(ui: Partial<Opportunity>): DbLeadCreate {
  return {
    primary_name:   ui.primaryName ?? "",
    secondary_name: ui.secondaryName || null,
    primary_phone:  ui.primaryPhone ?? "",
    secondary_phone: ui.secondaryPhone || "",
    primary_email:  ui.primaryEmail || null,
    secondary_email: ui.secondaryEmail || null,
    address: ui.address ?? "",
    city:    ui.city ?? "",
    state:   ui.state ?? "",
    zip:     ui.zip ?? "",
    project_type: ui.projectType ?? "Other",
    lead_source:  ui.leadSource ?? "",
    lead_source_id: null,
    stage:        (ui.stage ? LEAD_STAGE_TO_DB[ui.stage] : "Lead") as DbLead["stage"],
    assigned_rep: ui.assignedRep ?? null,
    ownership_history: serializeOwnershipHistory(ui.ownershipHistory),
    activities:   serializeActivities(ui.activities),
    stage_history: serializeStageHistory(ui.stageHistory),
    linked_quote_id: null,
    linked_job_id:   ui.linkedJobId ?? null,
    notes:    ui.notes || null,
    organization_id: ui.organizationId || null,
    branch_id: null,
    appointment_time: ui.appointmentTime || null,
  };
}

// ── UI → DB (update) ──────────────────────────────────────────────────────

export function toDbLeadUpdate(ui: Partial<Opportunity>): DbLeadUpdate {
  const patch: DbLeadUpdate = {};

  if (ui.primaryName !== undefined) patch.primary_name = ui.primaryName;
  if (ui.secondaryName !== undefined) patch.secondary_name = ui.secondaryName || null;
  if (ui.primaryPhone !== undefined) patch.primary_phone = ui.primaryPhone;
  if (ui.secondaryPhone !== undefined) patch.secondary_phone = ui.secondaryPhone || "";
  if (ui.primaryEmail !== undefined) patch.primary_email = ui.primaryEmail || null;
  if (ui.secondaryEmail !== undefined) patch.secondary_email = ui.secondaryEmail || null;
  if (ui.address !== undefined) patch.address = ui.address;
  if (ui.city !== undefined) patch.city = ui.city;
  if (ui.state !== undefined) patch.state = ui.state;
  if (ui.zip !== undefined) patch.zip = ui.zip;
  if (ui.projectType !== undefined) patch.project_type = ui.projectType;
  if (ui.leadSource !== undefined) patch.lead_source = ui.leadSource ?? "";
  if (ui.stage !== undefined) patch.stage = LEAD_STAGE_TO_DB[ui.stage] as DbLead["stage"];
  if (ui.assignedRep !== undefined) patch.assigned_rep = ui.assignedRep ?? null;
  if (ui.activities !== undefined) patch.activities = serializeActivities(ui.activities);
  if (ui.stageHistory !== undefined) patch.stage_history = serializeStageHistory(ui.stageHistory);
  if (ui.ownershipHistory !== undefined) patch.ownership_history = serializeOwnershipHistory(ui.ownershipHistory);
  if (ui.linkedJobId !== undefined) patch.linked_job_id = ui.linkedJobId ?? null;
  if (ui.organizationId !== undefined) patch.organization_id = ui.organizationId ?? null;
  if (ui.notes !== undefined) patch.notes = ui.notes || null;
  if (ui.appointmentTime !== undefined) patch.appointment_time = ui.appointmentTime || null;

  return patch;
}

// ── Batch ─────────────────────────────────────────────────────────────────

export function toUiOpportunities(rows: DbLead[]): Opportunity[] {
  return rows.map(toUiOpportunity);
}
